// src/components/auditor/AuditorDashboard.jsx
// COMPLETE FILE — Exact NIYANTRIX Landing Page replica

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Container, Typography, Paper, Button,
  TextField, IconButton, Chip, Skeleton,
  Stack, Alert, InputAdornment, LinearProgress,
  Divider, Snackbar,
} from "@mui/material";

import SearchIcon         from "@mui/icons-material/Search";
import ClearIcon          from "@mui/icons-material/Clear";
import EventNoteIcon      from "@mui/icons-material/EventNote";
import LocationOnIcon     from "@mui/icons-material/LocationOn";
import CalendarMonthIcon  from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import PendingIcon        from "@mui/icons-material/Pending";
import PlayArrowIcon      from "@mui/icons-material/PlayArrow";
import InventoryIcon      from "@mui/icons-material/Inventory";
import ArrowForwardIcon   from "@mui/icons-material/ArrowForward";

import { useAuth }          from "../../contexts/AuthContext";
import { fetchAuditEvents } from "../../services/auditService";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  initiated: {
    label: "Initiated", color: "#1d4ed8",
    bg: "#eff6ff", border: "#bfdbfe",
    icon: <PendingIcon sx={{ fontSize: 13 }} />,
  },
  in_progress: {
    label: "In Progress", color: "#92400e",
    bg: "#fffbeb", border: "#fde68a",
    icon: <PlayArrowIcon sx={{ fontSize: 13 }} />,
  },
  completed: {
    label: "Completed", color: "#15803d",
    bg: "#f0fdf4", border: "#bbf7d0",
    icon: <CheckCircleIcon sx={{ fontSize: 13 }} />,
  },
};

const FILTER_TABS = [
  { key: "all",         label: "All"         },
  { key: "initiated",   label: "Initiated"   },
  { key: "in_progress", label: "In Progress" },
  { key: "completed",   label: "Completed"   },
];

// ─── Audit Card ───────────────────────────────────────────────────────────────

const AuditCard = ({ event, onStart }) => {
  const cfg = STATUS_CFG[event.status] || STATUS_CFG.initiated;

  return (
    <Paper
      sx={{
        borderRadius: 2,
        mb: 1.5,
        overflow: "hidden",
        border: "1px solid #eef0f3",
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
        transition: "box-shadow 0.2s ease, transform .18s ease",
        "&:hover": {
          boxShadow: "0 8px 20px rgba(15,23,42,0.06)",
          transform: "translateY(-1px)",
        },
      }}
    >
      <Box
        sx={{
          px: 2, py: 1.5,
          bgcolor: cfg.bg,
          borderBottom: `1px solid ${cfg.border}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight={700} sx={{ flex: 1, lineHeight: 1.3, color: "#0f172a" }}>
          {event.title}
        </Typography>
        <Chip
          icon={cfg.icon}
          label={cfg.label}
          size="small"
          sx={{
            bgcolor: "#fff",
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
            fontWeight: 700,
            fontSize: "0.7rem",
            height: 24,
            flexShrink: 0,
            "& .MuiChip-icon": { color: cfg.color },
          }}
        />
      </Box>

      <Box sx={{ px: 2, py: 1.75 }}>
        <Stack direction="row" gap={2.5} flexWrap="wrap" sx={{ mb: 1.25 }}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <LocationOnIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
              {event.location_name || "—"}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <CalendarMonthIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
              {event.scheduled_date
                ? new Date(event.scheduled_date).toLocaleDateString("en-GB", {
                    day: "numeric", month: "short", year: "numeric",
                  })
                : "—"}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <InventoryIcon sx={{ fontSize: 14, color: "#64748b" }} />
            <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
              {event.doneAssets}/{event.totalAssets} assets
            </Typography>
          </Stack>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={event.progress || 0}
          sx={{
            height: 6, borderRadius: 3,
            bgcolor: "#eef2f7",
            "& .MuiLinearProgress-bar": {
              borderRadius: 3,
              bgcolor:
                event.progress === 100 ? "#16a34a"
                : event.progress > 50  ? "#2563eb"
                : "#d97706",
            },
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75 }}>
          <Typography variant="caption" sx={{ color: "#64748b" }}>
            {event.progress || 0}% complete
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color:
                event.progress === 100 ? "#16a34a"
                : event.progress > 50  ? "#2563eb"
                : "#d97706",
            }}
          >
            {event.doneAssets}/{event.totalAssets} done
          </Typography>
        </Box>
      </Box>

      {event.status !== "completed" ? (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.25 }}>
            <Button
              fullWidth
              variant={event.status === "initiated" ? "contained" : "outlined"}
              size="small"
              startIcon={event.status === "initiated" ? <PlayArrowIcon /> : <InventoryIcon />}
              onClick={() => onStart(event)}
              disableElevation
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.85rem",
                py: 1,
                bgcolor: event.status === "initiated" ? "#2563eb" : "transparent",
                borderColor: "#2563eb",
                color: event.status === "initiated" ? "#fff" : "#2563eb",
                "&:hover": {
                  bgcolor: event.status === "initiated" ? "#1d4ed8" : "#eff6ff",
                  borderColor: "#1d4ed8",
                },
              }}
            >
              {event.status === "initiated" ? "Start Audit" : "Continue Audit"}
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Divider />
          <Box sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1, bgcolor: "#f0fdf4" }}>
            <CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: "#15803d" }}>
              Audit Completed
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AuditorDashboard = () => {
  const navigate        = useNavigate();
  const { currentUser } = useAuth();

  const [showDashboard, setShowDashboard] = useState(false);
  const [auditEvents,  setAuditEvents]    = useState([]);
  const [loading,      setLoading]        = useState(true);
  const [searchTerm,   setSearchTerm]     = useState("");
  const [activeFilter, setActiveFilter]   = useState("all");
  const [snack,        setSnack]          = useState(null);

  const showSnack = useCallback((message, severity = "success") => {
    setSnack({ message, severity });
    setTimeout(() => setSnack(null), 4000);
  }, []);

  const load = useCallback(async () => {
    if (!currentUser?.companyId) return;
    setLoading(true);
    try {
      const all  = await fetchAuditEvents(currentUser.companyId);
      const mine = all.filter((e) => e.auditor_id === currentUser.uid);
      setAuditEvents(mine);
    } catch {
      showSnack("Failed to load audits", "error");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.companyId, currentUser?.uid, showSnack]);

  useEffect(() => { load(); }, [load]);

  const tabCounts = useMemo(() => ({
    all:         auditEvents.length,
    initiated:   auditEvents.filter((e) => e.status === "initiated").length,
    in_progress: auditEvents.filter((e) => e.status === "in_progress").length,
    completed:   auditEvents.filter((e) => e.status === "completed").length,
  }), [auditEvents]);

  const filtered = useMemo(() => {
    let result = auditEvents;
    if (activeFilter !== "all") {
      result = result.filter((e) => e.status === activeFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (e) =>
          e.title?.toLowerCase().includes(q) ||
          e.location_name?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [auditEvents, activeFilter, searchTerm]);

  // ═══════════════════════════════════════════════════════════
  // LANDING VIEW — Exact replica of screenshot
  // ═══════════════════════════════════════════════════════════
  if (!showDashboard) {
    const features = [
      { title: "QR Automation",     desc: "Automated QR code generation and scanning." },
      { title: "AI Maintenance",    desc: "AI-driven predictive maintenance and renewals." },
      { title: "RBAC",              desc: "Role-based access for Super Admin, Admin, and Users." },
      { title: "Location Tracking", desc: "Location and department-based tracking." },
      { title: "Audit Logs",        desc: "Integrated audit logs and activity monitoring." },
    ];

    return (
      <Box sx={{ bgcolor: "#f6f8fb", minHeight: "100vh" }}>
        {/* ══════════ HERO ══════════ */}
        <Container maxWidth="lg" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 6 } }}>
          <Stack alignItems="center" spacing={3}>
            {/* Logo card */}
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                px: { xs: 3, sm: 5 },
                py: { xs: 2.5, sm: 3 },
                boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="NIYANTRIX"
                sx={{
                  width: { xs: 55, sm: 70 },
                  height: { xs: 55, sm: 70 },
                  objectFit: "contain",
                }}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "1.8rem", sm: "2.4rem" },
                    fontWeight: 800,
                    color: "#1e3a8a",
                    letterSpacing: "1px",
                    lineHeight: 1,
                  }}
                >
                  NIYANTRIX
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "0.7rem", sm: "0.8rem" },
                    fontWeight: 700,
                    color: "#1e3a8a",
                    letterSpacing: "2px",
                    mt: 0.75,
                  }}
                >
                  ASSET MANAGEMENT SOFTWARE
                </Typography>
              </Box>
            </Box>

            {/* Title */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: "#0f172a",
                textAlign: "center",
                fontSize: { xs: "1.6rem", sm: "2rem", md: "2.25rem" },
                mt: 2,
              }}
            >
              NIYANTRIX Asset Management
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                color: "#64748b",
                textAlign: "center",
                fontSize: { xs: "0.95rem", sm: "1.05rem" },
              }}
            >
              Scalable Solutions for Modern Enterprises
            </Typography>

            {/* Proceed CTA */}
            <Button
              onClick={() => navigate('/auditor/active-audits')}
              endIcon={<ArrowForwardIcon />}
              sx={{
                mt: 2,
                bgcolor: "#2563eb",
                color: "#fff",
                fontWeight: 700,
                fontSize: "1rem",
                textTransform: "none",
                borderRadius: 999,
                px: 4,
                py: 1.5,
                boxShadow: "0 6px 16px rgba(37,99,235,0.35)",
                "&:hover": {
                  bgcolor: "#1d4ed8",
                  boxShadow: "0 8px 20px rgba(37,99,235,0.45)",
                },
              }}
            >
              Proceed
            </Button>
          </Stack>
        </Container>

        {/* ══════════ FEATURE GRID ══════════ */}
        <Container maxWidth="lg" sx={{ pb: { xs: 5, md: 8 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: { xs: 2, sm: 2.5, md: 3 },
            }}
          >
            {features.map((feat, i) => (
              <Paper
                key={i}
                sx={{
                  p: { xs: 3, sm: 3.5 },
                  borderRadius: 2,
                  border: "1px solid #eef0f3",
                  boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
                  bgcolor: "#fff",
                  transition: "transform .18s ease, box-shadow .18s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.35rem", sm: "1.5rem" },
                    fontWeight: 700,
                    color: "#0f172a",
                    mb: 1.5,
                  }}
                >
                  {feat.title}
                </Typography>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.95rem",
                    lineHeight: 1.5,
                  }}
                >
                  {feat.desc}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>

        {/* ══════════ FOOTER ══════════ */}
        <Box
          component="footer"
          sx={{
            bgcolor: "#fff",
            borderTop: "1px solid #eef0f3",
            py: { xs: 4, md: 5 },
            textAlign: "center",
          }}
        >
          <Container maxWidth="lg">
            <Typography
              sx={{
                fontSize: { xs: "1.25rem", sm: "1.4rem" },
                fontWeight: 700,
                color: "#0f172a",
                mb: 1.5,
              }}
            >
              Connect With Our Team
            </Typography>
            <Typography sx={{ color: "#475569", mb: 2, fontSize: "1rem" }}>
              Dedicated Support:{" "}
              <Box component="strong" sx={{ color: "#0f172a", fontWeight: 700 }}>
                +91 6360919373
              </Box>
            </Typography>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.5, sm: 1.5 }}
              justifyContent="center"
              alignItems="center"
              divider={
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    color: "#cbd5e1",
                  }}
                >
                  |
                </Box>
              }
            >
              <Typography
                component="span"
                sx={{
                  color: "#2563eb",
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Schedule Live Demo
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: "#2563eb",
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Enterprise API
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: "#2563eb",
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Partnership Opportunities
              </Typography>
            </Stack>
          </Container>
        </Box>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // DASHBOARD VIEW — Shows after clicking "Proceed to Dashboard"
  // ═══════════════════════════════════════════════════════════
  return (
    <Box sx={{ bgcolor: "#f6f8fb", minHeight: "100vh", pb: 6 }}>
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {snack && (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        )}
      </Snackbar>

      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back to landing */}
        <Button
          onClick={() => setShowDashboard(false)}
          sx={{
            mb: 2,
            textTransform: "none",
            color: "#64748b",
            fontWeight: 500,
          }}
        >
          ← Back to Home
        </Button>

        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
            My Audits
          </Typography>
          <Typography sx={{ color: "#64748b" }}>
            {currentUser?.displayName
              ? `Welcome back, ${currentUser.displayName}`
              : "Your assigned audit events"}
          </Typography>
        </Box>

        {/* Search + Filters */}
        <Paper
          sx={{
            p: { xs: 2, sm: 2.5 },
            borderRadius: 2,
            border: "1px solid #eef0f3",
            boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
            mb: 2.5,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems={{ md: "center" }}>
            <TextField
              size="small"
              placeholder="Search by audit title or location…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 999,
                  bgcolor: "#f8fafc",
                  "& fieldset": { borderColor: "#e5e7eb" },
                },
              }}
            />

            <Box
              sx={{
                display: "flex", gap: 1,
                overflowX: "auto",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {FILTER_TABS.map((tab) => (
                <Button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  size="small"
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: activeFilter === tab.key ? 700 : 500,
                    fontSize: "0.8rem",
                    px: 2,
                    py: 0.75,
                    whiteSpace: "nowrap",
                    bgcolor: activeFilter === tab.key ? "#2563eb" : "#fff",
                    color:   activeFilter === tab.key ? "#fff"    : "#475569",
                    border:  activeFilter === tab.key ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    "&:hover": {
                      bgcolor: activeFilter === tab.key ? "#1d4ed8" : "#f8fafc",
                    },
                  }}
                >
                  {tab.label}
                  <Box
                    component="span"
                    sx={{
                      ml: 0.75, px: 0.85,
                      borderRadius: 999,
                      bgcolor: activeFilter === tab.key ? "rgba(255,255,255,0.25)" : "#eef2f7",
                      color:   activeFilter === tab.key ? "#fff" : "#475569",
                      fontSize: "0.7rem", fontWeight: 700,
                    }}
                  >
                    {tabCounts[tab.key]}
                  </Box>
                </Button>
              ))}
            </Box>
          </Stack>
        </Paper>

        {/* Audit list */}
        {loading ? (
          [1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" sx={{ height: 180, borderRadius: 2, mb: 1.5 }} />
          ))
        ) : filtered.length === 0 ? (
          <Paper
            sx={{
              borderRadius: 2,
              p: { xs: 4, sm: 6 },
              textAlign: "center",
              border: "1px solid #eef0f3",
            }}
          >
            <EventNoteIcon sx={{ fontSize: 52, color: "#cbd5e1", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#475569", fontWeight: 600, mb: 0.5 }}>
              {searchTerm
                ? "No audits match your search"
                : activeFilter !== "all"
                ? `No ${STATUS_CFG[activeFilter]?.label || ""} audits`
                : "No audits assigned to you yet"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8" }}>
              {searchTerm
                ? "Try a different search term"
                : "Your admin will assign audit events to you"}
            </Typography>
          </Paper>
        ) : (
          filtered.map((event) => (
            <AuditCard
              key={event.id}
              event={event}
              onStart={(e) => navigate(`/auditor/audit/${e.id}`)}
            />
          ))
        )}
      </Container>
    </Box>
  );
};

export default AuditorDashboard;